import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '@/services/api';
import { User } from '@/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;  // 사용자 정보 새로고침
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const userData = await authAPI.getCurrentUser();
          setUser(userData);
          setToken(storedToken);
        } catch (error: any) {
          console.error('Failed to get current user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        // localStorage에 토큰이 없어도 쿠키에 있을 수 있음 (Google OAuth 등)
        // 쿠키에서 토큰을 확인하기 위해 /api/auth/me 호출 시도
        try {
          const userData = await authAPI.getCurrentUser();
          // 쿠키에서 토큰이 있는 경우, 사용자 정보를 가져올 수 있음
          // 하지만 토큰 자체는 httponly 쿠키이므로 JavaScript에서 읽을 수 없음
          // 따라서 사용자 정보만 설정하고, token은 null로 유지
          // (API 호출 시 자동으로 쿠키가 포함됨)
          setUser(userData);
          setToken('cookie'); // 쿠키에 토큰이 있음을 표시 (실제 토큰 값은 아니지만 인증 상태를 나타냄)
        } catch (error: any) {
          // 쿠키에도 토큰이 없는 경우 (정상적인 비로그인 상태)
          // 에러를 무시하고 계속 진행
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (newToken: string, userData: User) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      // 백엔드에 로그아웃 요청 (쿠키 삭제)
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // 사용자 정보 새로고침 (관리자가 권한 변경 시 사용)
  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
      } catch (error: any) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    refreshUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
